import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    vertical: {
      reflect: true,
    },
    thumbs: {
      type: Boolean,
      reflect: true,
    },
    dots: {
      reflect: true,
    },
    _advance: {
      state: true,
    },
    _scrollDirection: {
      state: true,
    },
    _direction: {
      state: true,
    },
    'slide-width': {
      reflect: true,
    },
    reverse: {
      reflect: true,
    },
    _offsetIndex: {
      state: true,
    },
    _offsetIndexMobile: {
      state: true,
    },
    _offsetBreakpoint: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()
  $thumbScrollWrapper = createRef()

  constructor() {
    super()

    this.thumbs = false
    this._current = 1
    this._advance = 0
    // ctx="gallery" only: last active slide index (see updateUI).
    this._lastGalleryActiveIdx = null

    this.updateUI = this.updateUI.bind(this)
    this.scrollTo = this.scrollTo.bind(this)

    this.calculateOffsetIndex()
  }

  calculateOffsetIndex() {
    this._offsetIndex = Number(this.getAttribute('offsetIndex')) || 1
    this._offsetIndexMobile =
      Number(this.getAttribute('offsetIndexMobile')) || 1
    this._offsetBreakpoint = this.getAttribute('offsetBreakpoint') || '750'
  }

  updateScrollButtons() {
    const el = this.$thumbScrollWrapper?.value
    if (!el) return

    const scrollTop = el.scrollTop
    const scrollHeight = el.scrollHeight
    const offsetHeight = el.offsetHeight

    this.canScrollUp = scrollTop > 0
    this.canScrollDown = scrollTop + offsetHeight < scrollHeight

    this.requestUpdate()
  }

  scrollThumbnails(direction) {
    const el = this.$thumbScrollWrapper?.value
    if (!el) return

    const firstThumb = el.querySelector('li')
    if (!firstThumb) return

    const thumbStyles = window.getComputedStyle(firstThumb)
    const height =
      firstThumb.offsetHeight +
      parseFloat(thumbStyles.marginTop) +
      parseFloat(thumbStyles.marginBottom)

    const delta = direction === 'up' ? -height : height

    el.scrollBy({
      top: delta,
      behavior: 'smooth',
    })

    setTimeout(() => this.updateScrollButtons(), 300)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.updateUI()
      if (
        this.isVertical() &&
        this.showDots() &&
        this.getAttribute('dots-style') === 'line' &&
        this.thumbs
      ) {
        this.updateScrollButtons()
      }
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
      this.addEventListener('hh-slider:scroll-to', this.scrollTo)
      if (this['slide-width']) {
        const activeItemIndex = Array.from(
          this.querySelectorAll('[slot="item"]')
        ).findIndex((item) => item.classList.contains('is-active'))
        if (!~activeItemIndex) return
        this.move(null, activeItemIndex)
      }

      this.addEventListener('keydown', this.handleKeydown)
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.removeEventListener('hh-slider:scroll-to', this.scrollTo)
    this.observer.disconnect()
    this.removeEventListener('keydown', this.handleKeydown)
  }

  get _total() {
    return this.activeItems.length
  }

  get activeItems() {
    return this.items.filter((item) => !item.classList.contains('hidden'))
  }

  handleKeydown(event) {
    let slideIndex = 1
    switch (event.key.toLowerCase()) {
      case 'tab':
        const target = event.target.closest('[slot="item"]')
        if (!target) return
        slideIndex = [...target.parentElement.children].indexOf(target)
        if (slideIndex === -1) return
        this.move(null, slideIndex)
        break
      case 'arrowleft':
        if (this.isVertical()) break
        slideIndex = this._current - 1
        if (slideIndex === -1) return
        this.move(null, slideIndex)
        break
      case 'arrowright':
        if (this.isVertical()) break
        slideIndex =
          this._current -
          (window.matchMedia(`(min-width: ${this._offsetBreakpoint}px)`).matches
            ? this._offsetIndex
            : this._offsetIndexMobile)
        this.move(null, slideIndex)
        break
      case 'arrowup':
        if (!this.isVertical()) break
        slideIndex = this._current - 1
        if (slideIndex === -1) return
        this.move(null, slideIndex)
        break
      case 'arrowdown':
        if (!this.isVertical()) break
        slideIndex = this._current + 1
        this.move(null, slideIndex)
        break
      default:
        break
    }
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total || !this.$container.value) {
      return
    }

    this._advance = this.isVertical()
      ? this.activeItems[0].clientHeight
      : this.activeItems[0].clientWidth
    this._scrollDirection = this.isVertical() ? 'scrollTop' : 'scrollLeft'
    this._direction = this.isVertical() ? 'top' : 'left'
    this._current =
      Math.round(
        (this.$container.value[this._scrollDirection] + 40) / this._advance
      ) +
      (window.matchMedia(`(min-width: ${this._offsetBreakpoint}px)`).matches
        ? this._offsetIndex
        : this._offsetIndexMobile)

    // ctx="gallery": pause deferred-media[data-video-controls] on slides that aren't active anymore.
    if (
      this.getAttribute('ctx') === 'gallery' &&
      this._advance > 0
    ) {
      const offset = window.matchMedia(
        `(min-width: ${this._offsetBreakpoint}px)`
      ).matches
        ? this._offsetIndex
        : this._offsetIndexMobile
      let activeIdx = this._current - offset
      const max = this.activeItems.length - 1
      if (max < 0) return
      activeIdx = Math.max(0, Math.min(activeIdx, max))
      if (activeIdx !== this._lastGalleryActiveIdx) {
        this._lastGalleryActiveIdx = activeIdx
        this.pauseInactiveVideoControls(activeIdx)
        this.playActiveGalleryMedia(activeIdx)
      }
    }
  }

  playActiveGalleryMedia(activeIdx) {
    const item = this.activeItems[activeIdx]
    if (!item) return
    requestAnimationFrame(() => {
      item
        .querySelectorAll('deferred-media[data-video-controls]')
        .forEach((dm) => {
          if (typeof dm.ensureLoadedAndPlay === 'function') {
            dm.ensureLoadedAndPlay()
          }
        })
    })
  }

  // Any deferred-media with data-video-controls can be paused when its slide isn't active.
  pauseInactiveVideoControls(activeIdx) {
    this.activeItems.forEach((item, i) => {
      if (i === activeIdx) return
      item.querySelectorAll('deferred-media[data-video-controls]').forEach((dm) => {
        if (typeof dm.pauseAndReset === 'function') {
          dm.pauseAndReset()
        }
      })
    })
  }

  move(event, index = false) {
    event?.preventDefault()
    if (!this.$container.value) return
    const reverseIndex = this.reverse === 'true' ? -1 : 1
    const slideScrollPosition =
      index !== false
        ? this._advance * index
        : event.currentTarget.name === 'next'
          ? this.$container.value[this._scrollDirection] + this._advance
          : this.$container.value[this._scrollDirection] - this._advance
    if (!this.active) {
      const mediaOffsetTop = this.items.find(
        (item) => Number(item.dataset.index) === index
      ).offsetTop
      return window.scrollTo({
        top: mediaOffsetTop,
        behavior: 'smooth',
      })
    }
    this.$container?.value?.scrollTo({
      [this._direction]: slideScrollPosition * reverseIndex,
    })
  }

  scrollTo(event) {
    this.move(null, event.detail.index)
  }

  isVertical() {
    return [true, 'true'].includes(this.vertical)
  }

  showDots() {
    return [true, 'true'].includes(this.dots)
  }

  renderDot(index) {
    let dot_class = ''
    if (index < this._current - 1) {
      dot_class = 'active'
    } else if (index === this._current - 1) {
      dot_class = 'active is-current'
    }

    const slotItem = this.items[index]
    let imgHTML = ''

    if (slotItem && this.thumbs) {
      const img = slotItem.querySelector('img')
      if (img) {
        imgHTML = `<img class="thumb" src="${img.src}" alt="${img.alt}" loading="lazy" />`
      }
    }

    return html`
      <li>
        <button
          class="${dot_class}"
          @click="${(event) => this.move(event, index)}"
        >
          <span class="visually-hidden">${index}</span>
          ${unsafeHTML(imgHTML)}
        </button>
      </li>
    `
  }

  renderDots() {
    if (!this.showDots()) return html``

    const isVerticalWithThumbs =
      this.isVertical() &&
      this.getAttribute('dots-style') === 'line' &&
      this.thumbs

    const showScrollButtons = isVerticalWithThumbs && this._total > 6

    if (!isVerticalWithThumbs) {
      // fallback: regular dot navigation for mobile
      return html`<div class="dots">
        <ul role="list">
          ${[...Array(this._total).keys()].map((index) =>
        this.renderDot(index)
      )}
        </ul>
      </div>`
    }

    // ↓ scrollable thumbnail nav version
    return html`
      <div class="dots">
        ${showScrollButtons
        ? html`
              <button
                class="thumb-nav thumb-nav--up"
                @click="${() => this.scrollThumbnails('up')}"
                ?disabled="${!this.canScrollUp}"
                aria-label="Scroll up thumbnails"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            `
        : null}

        <div
          class="dot-scroll-wrapper ${showScrollButtons ? 'has-scroll' : ''}"
          ${ref(this.$thumbScrollWrapper)}
        >
          <ul role="list">
            ${[...Array(this._total).keys()].map((index) =>
          this.renderDot(index)
        )}
          </ul>
        </div>

        ${showScrollButtons
        ? html`
              <button
                class="thumb-nav thumb-nav--down"
                @click="${() => this.scrollThumbnails('down')}"
                ?disabled="${!this.canScrollDown}"
                aria-label="Scroll down thumbnails"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            `
        : null}
      </div>
    `
  }

  refreshItems() {
    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this.items.forEach((item, index) => {
      item.dataset.index = index
    })

    this.requestUpdate()
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)}>
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this.__current === this._total}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>
      ${this.renderDots()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        display: block;
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-y: hidden;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        scrollbar-height: none; /* Firefox */
      }

      :host([active='true']) .container::-webkit-scrollbar {
        width: 0; /* Remove scrollbar space */
        height: 0; /* Remove scrollbar space */
        background: transparent; /* Optional: just make scrollbar invisible */
        display: none;
      }

      :host([active='true'][slide-width='variable']) .container {
        gap: 1rem;
        padding-bottom: 1rem;
      }

      @media screen and (min-width: 750px) {
        :host([active='true'][slide-width='variable']) .container {
          gap: 5rem;
        }
      }

      :host([active='true'][reverse='true']) .container {
        flex-direction: row-reverse;
      }

      :host([active='false']) .container {
        gap: 0.5rem;
      }

      :host([active='true'][vertical='true']) .container {
        aspect-ratio: var(--hh-vertical-aspect-ratio);
        flex-wrap: wrap;
        overflow-y: auto;
        overflow-x: hidden;
        padding-left: var(--hh-vertical-padding-left, 70px);
        scroll-snap-type: y mandatory;
      }

      @media screen and (max-width: 749px) {
        :host([active='true'][vertical='true'][ctx='gallery']) .container {
          aspect-ratio: unset;
          height: var(--hh-vertical-height, 140vw);
          max-height: var(--hh-vertical-max-height, 140vw);
        }
      }

      :host([active='true']) ::slotted(*) {
        flex: 1 0 100%;
        scroll-snap-align: start;
      }

      :host([active='true'][slide-width='variable']) ::slotted(*) {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
      }

      :host([active='false']) ::slotted(*) {
        flex: 1 0 100%;
      }

      :host([active='false']) ::slotted(*:nth-child(3n + 2)),
      :host([active='false']) ::slotted(*:nth-child(3n + 3)) {
        flex: 1 0 calc(50% - 0.5rem);
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active='true'][buttons='true']) .buttons {
        display: flex;
      }

      .dots {
        display: none;
        position: absolute;
        z-index: 3;
        bottom: 20px;
        left: 0;
        right: 0;
      }

      :host([vertical='true']) .dots {
        top: 50%;
        bottom: auto;
        right: auto;
        left: 10px;
        transform: translateY(-50%);
      }

      :host([active='true'][dots='true']) .dots {
        display: block;
      }

      .dots ul {
        list-style-type: none;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
      }

      :host([vertical='true']) .dots ul {
        display: block;
      }

      .dots li {
        margin: 0 5px;
      }

      :host([vertical='true']) .dots li {
        margin: 5px 0;
      }

      .dots button {
        background: none;
        width: 5px;
        height: 5px;
        padding: 0;
        border: 1px solid var(--hh-color-brand-tertiary);
        border-radius: 0;
        position: relative;
        z-index: 1;
      }

      .dots button.is-current {
        background: var(--hh-color-brand-tertiary);
        border-radius: 50%;
      }

      @media (max-width: 600px) {
        .dots button {
          border: 1px solid #000;
          border-radius: 50%;
        }
        .dots button.is-current {
          background: #000;
        }
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: rotate(90deg);
      }

      .button--next {
        margin-left: -0.2rem;
        transform: rotate(-90deg);
      }

      :host([vertical='true'][dots-style='line']) .dots {
        top: 0;
        left: unset;
        transform: unset;
      }

      :host([vertical='true'][dots-style='line']) .dots ul {
        display: flex;
        gap: var(--hh-thumb-gap, 2rem);
        flex-direction: column;
      }

      :host([vertical='true'][dots-style='line']) .dots li {
        margin: 0;
        display: block;
        flex: 0 0 auto;
        height: var(--hh-thumb-height, 6rem);
        width: var(--hh-thumb-width, 4.5rem);
      }

      :host([vertical='true'][dots-style='line']) .dots .thumb {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      :host([vertical='true'][dots-style='line']) .dots button {
        cursor: pointer;
        flex: 1;
        position: relative;
        border: 0;
        border-radius: 0;
        width: 100%;
        height: 100%;
      }

      :host([vertical='true'][dots-style='line']) .dots button.is-current {
        outline: 1px solid var(--hh-color-focus);
        outline-offset: 2px;
      }

      @media (max-width: 749px) {
        :host([vertical='true'][dots-style='line']) .dots {
          top: unset;
          bottom: var(--hh-dots-line-bottom-mobile, 1.6rem);
          left: var(--hh-dots-line-left, 2rem);
          right: unset;
          transform: unset;
        }

        .thumb {
          display: none;
        }
      }

      :host([vertical='true'][dots-style='line']) .dot-scroll-wrapper {
        padding: 0 0.5rem;
        max-height: var(--hh-thumb-max-height, none);
        overflow-y: var(--hh-thumb-overflow, visible);
        scrollbar-width: none;
      }

      :host([vertical='true'][dots-style='line'])
        .dot-scroll-wrapper::-webkit-scrollbar {
        width: 0;
        display: none;
      }

      :host([vertical='true'][dots-style='line'])
        .dot-scroll-wrapper.has-scroll {
        max-height: var(--hh-thumb-max-height, 48rem);
        overflow-y: hidden;
      }

      :host([vertical='true'][dots-style='line'])
        .dot-scroll-wrapper.has-scroll
        ul {
        gap: 0;
      }

      :host([vertical='true'][dots-style='line'])
        .dot-scroll-wrapper.has-scroll
        li {
        margin: calc(var(--hh-thumb-gap, 2rem) / 2) 0;
      }

      :host([vertical='true'][dots-style='line']) .thumb-nav {
        display: block;
        background: none;
        border: none;
        cursor: pointer;
        width: 100%;
        font-size: 1.5rem;
        color: var(--hh-color);
        padding: 0.5rem 0;
      }

      :host([vertical='true'][dots-style='line']) .thumb-nav svg {
        width: 12px;
        margin: 0 auto;
      }

      :host([vertical='true'][dots-style='line']) .thumb-nav.thumb-nav--up svg {
        transform: rotate(180deg);
      }

      :host([vertical='true'][dots-style='line']) .thumb-nav[disabled] {
        opacity: 0.3;
        cursor: default;
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
